/**
 * The Clinic shell — top bar with permanent clinic context, labelled
 * start-side rail (desktop), icon+label rail (tablet), bottom bar (mobile).
 * Navigation is permission-shaped: a destination a role may never open is
 * ABSENT, not disabled (frozen rule). No notification bell (P11: the Clinic
 * backend has no notification domain; no dependency may form on one). The
 * top bar's one global action is Sign out — one tap, cheap and reversible,
 * no confirm dialog (frozen decision).
 *
 * ZOY-614/616: the context tile carries the SELECTED clinic's logo when it
 * has one — a short-lived URL minted per request, keyed by the clinic and
 * purged on a switch, so a stale image can never belong to another clinic.
 * The clinic's initials remain the fallback (no logo, or the image fails).
 */

import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import s from './shell.module.css';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { useSession } from '@/core/auth/SessionProvider';
import { clinicRoleOf, useMyIdentity, useMyPersonnel } from '@/core/auth/useMyIdentity';
import { PERM, type PermissionKey } from '@/core/authz/permissions';
import { fetchClinic, clinicDisplayName, fetchClinicLogo } from '@/core/clinic/api';
import { signedOutDestination } from '@/features/entry/entryIntent';
import { qk } from '@/core/api/keys';
import { usePendingRequestsCount } from '@/features/family/usePendingRequestsCount';

interface NavDef {
  id: string;
  to: string;
  labelId: string;
  icon: string;
  /** null = everyone; otherwise the rendering permission key. */
  perm: PermissionKey | null;
}

const NAV: readonly NavDef[] = [
  // ZOY-702: first, because it is where its holders land. Absent without the key.
  {
    id: 'dashboard',
    to: '/reports',
    labelId: 'nav.dashboard',
    perm: PERM.reportsRead,
    icon: 'M4 20V10|M10 20V4|M16 20v-7|M22 20H2',
  },
  {
    id: 'today',
    to: '/today',
    labelId: 'nav.today',
    perm: null,
    icon: 'M4 13a8 8 0 0 1 16 0|M12 8v5l3 2',
  },
  {
    id: 'calendar',
    to: '/calendar',
    labelId: 'nav.calendar',
    perm: PERM.bookingsRead,
    icon: 'M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5z|M4 9.5h16|M8.5 4v3|M15.5 4v3',
  },
  // ZOY-712: reception's worklist, its own destination (never under Family or Setup).
  {
    id: 'followups',
    to: '/follow-ups',
    labelId: 'nav.followups',
    perm: PERM.familiesRead,
    icon: 'M9 6h11|M9 12h11|M9 18h11|M4 6l1 1 2-2|M4 12l1 1 2-2|M4 18l1 1 2-2',
  },
  {
    id: 'patients',
    to: '/patients',
    labelId: 'nav.patients',
    perm: PERM.patientsRead,
    icon: 'M9 11a3.4 3.4 0 1 0 0-6.8A3.4 3.4 0 0 0 9 11z|M3 19.5a6 6 0 0 1 12 0|M17 10.5a2.6 2.6 0 1 0 0-5.2|M16 14.4a4.8 4.8 0 0 1 5 4.6',
  },
  {
    id: 'payments',
    to: '/payments',
    labelId: 'nav.payments',
    perm: PERM.sessionsRead,
    icon: 'M3.5 8.5h17v9a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z|M3.5 12.5h17|M7 16.5h3',
  },
  {
    id: 'family',
    to: '/family',
    labelId: 'nav.family',
    perm: PERM.familiesRead,
    icon: 'M12 20.5s-7-4.6-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10.5c0 5.4-7 10-7 10z',
  },
  {
    id: 'clinic-setup',
    to: '/clinic-setup',
    labelId: 'nav.clinicSetup',
    perm: PERM.settingsRead,
    icon: 'M4 20V9.5L12 4l8 5.5V20|M9.5 20v-6h5v6',
  },
];

function NavIcon({ paths, size = 19 }: { paths: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {paths.split('|').map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

export function Shell() {
  const intl = useIntl();
  const authz = useAuthz();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const navigate = useNavigate();
  const { signOut } = useSession();

  const clinicQuery = useQuery({
    queryKey: authz.clinicId ? qk.clinic(authz.clinicId) : ['clinic', 'none'],
    queryFn: () => fetchClinic(),
    enabled: authz.clinicId !== null,
    staleTime: 10 * 60_000,
  });

  const items = NAV.filter((n) => n.perm === null || authz.can(n.perm));
  // ZOY-665: the pending family requests — the total the requests read already
  // carries, refetched on focus and after family writes; never polled.
  const pendingRequests = usePendingRequestsCount();
  const badgeOf = (id: string) =>
    id === 'family' && pendingRequests !== null && pendingRequests > 0 ? pendingRequests : null;
  const isActive = (to: string) => pathname.startsWith(to);

  // A 404 here means "this clinic has no logo" — a fact, not a failure, so it
  // is never retried and the initials simply stay.
  const logoQuery = useQuery({
    queryKey: authz.clinicId ? ['clinic-logo', authz.clinicId] : ['clinic-logo', 'none'],
    queryFn: () => fetchClinicLogo(),
    enabled: authz.clinicId !== null,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = logoQuery.data?.url ?? null;
  useEffect(() => setLogoFailed(false), [logoUrl]);

  const clinicName = clinicQuery.data ? clinicDisplayName(clinicQuery.data) : '…';
  const initials = clinicName.trim().slice(0, 2);
  const identity = useMyIdentity();
  const personnel = useMyPersonnel();
  // One line under the name: the clinic job title from the personnel profile
  // (the same field the calendar and rosters show) — never a permission key,
  // never an Auth RBAC role name.
  const caption = personnel.status === 'ready' ? clinicRoleOf(personnel.view) : null;

  return (
    <div className={s.frame}>
      <header className={s.topbar}>
        <div className={s.ctx}>
          {logoUrl && !logoFailed ? (
            <img
              className={s.ctxLogo}
              src={logoUrl}
              alt={clinicName}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <span className={s.ctxTile} aria-hidden>
              {initials}
            </span>
          )}
          <span>
            <span className={s.ctxLabel}>{intl.formatMessage({ id: 'app.clinicTag' })}</span>
            <div className={s.ctxName}>{clinicName}</div>
          </span>
        </div>
        <div className={s.topSpacer} />
        {/* The user guides — beside Sign out for every role; the label yields to the icon on phones. */}
        <Link to="/help" className={s.iconBtn} aria-label={intl.formatMessage({ id: 'help.nav' })}>
          <NavIcon
            paths="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2 2 0 0 1 2 2v14a1.5 1.5 0 0 0-1.5-1.5H4z|M20 5.5A1.5 1.5 0 0 0 18.5 4H14a2 2 0 0 0-2 2v14a1.5 1.5 0 0 1 1.5-1.5H20z"
            size={17}
          />
          <span className={s.iconBtnLabel}>{intl.formatMessage({ id: 'help.nav' })}</span>
        </Link>
        <button
          type="button"
          className={s.iconBtn}
          onClick={() => {
            const dest = signedOutDestination();
            void signOut().then(() => navigate({ to: dest }));
          }}
        >
          <NavIcon
            paths="M10 4H6.5A2.5 2.5 0 0 0 4 6.5v11A2.5 2.5 0 0 0 6.5 20H10|M15 8l4 4-4 4|M19 12H9"
            size={17}
          />
          {intl.formatMessage({ id: 'account.signOut' })}
        </button>
        <Link to="/account" className={s.userBtn}>
          <span className={s.userTile} aria-hidden>
            {identity.initials}
          </span>
          <span className={s.userMeta}>
            {/* The person's name; the opaque uid is a machine fact and never the primary identity. */}
            <div className={s.userName} dir="auto">
              {identity.name ??
                (identity.loading ? '…' : intl.formatMessage({ id: 'account.noName' }))}
            </div>
            {caption && <div className={s.userRole}>{caption}</div>}
          </span>
        </Link>
      </header>

      <div className={s.body}>
        <nav className={s.rail} aria-label="Primary">
          {items.map((n) => (
            <Link
              key={n.id}
              to={n.to}
              className={[s.railItem, isActive(n.to) ? s.railItemOn : ''].join(' ')}
              aria-current={isActive(n.to) ? 'page' : undefined}
            >
              <span className={s.railIcon}>
                <NavIcon paths={n.icon} />
              </span>
              {intl.formatMessage({ id: n.labelId })}
              {badgeOf(n.id) !== null && (
                <span
                  className={s.badge}
                  aria-label={intl.formatMessage(
                    { id: 'family.ws.pending' },
                    { n: badgeOf(n.id)! },
                  )}
                >
                  {intl.formatNumber(badgeOf(n.id)!)}
                </span>
              )}
            </Link>
          ))}
          <div className={s.railFoot}>{intl.formatMessage({ id: 'nav.railNote' })}</div>
        </nav>

        <main className={s.workspace}>
          <Outlet />
        </main>
      </div>

      <nav className={s.bottomNav} aria-label="Primary">
        {/* Every destination stays reachable: up to five share the bar as before,
            more than five scroll inside it (ZOY-702 added a possible seventh). */}
        {items.map((n) => (
          <Link
            key={n.id}
            to={n.to}
            className={[s.bottomItem, isActive(n.to) ? s.bottomItemOn : ''].join(' ')}
            aria-current={isActive(n.to) ? 'page' : undefined}
          >
            <NavIcon paths={n.icon} size={20} />
            {intl.formatMessage({ id: n.labelId })}
            {badgeOf(n.id) !== null && (
              <span className={s.badge} aria-hidden>
                {intl.formatNumber(badgeOf(n.id)!)}
              </span>
            )}
          </Link>
        ))}
      </nav>
    </div>
  );
}
